import React, { useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../../models/stores/Root';
import AuthenticatedPaymentMethodForm from '../../../components/input/AuthenticatedPaymentMethodForm';
import InternalLink from '../../../components/input/InternalLink';
import { ConfigEnabledPaymentMethodContract } from '../../../types/Configuration';
import { PaymentProfileUpdateConfig } from '../../../models/stores/Cart';
import PaymentMethodForm from '../../../components/input/PaymentMethodForm';
import {
  PayPalAcpHostedFieldsResponseData,
  PayPalAcpMethods,
  PayPalOnApproveData,
  PayPalOnClickData,
} from '../../../types/PayPal';
import Button from '../../../components/input/Button';
import { getPaymentSchema } from '../../../models/schemas/Payment';
import {
  CartPaymentProfileCardContract,
  CartPaymentProfileContract,
} from '../../../types/PaymentProfile';
import {
  SpreedlyValues,
  UIConstants,
  APIValues,
  SiteLimitationKeys,
} from '../../../utils/constants';
import UtilityService from '../../../services/UtilityService';
import FormErrorLabel from '../../../components/input/FormErrorLabel';
import GiftCardSection from '../../../components/framework/GiftCardSection';
import Spacer from '../../../components/content/Spacer';
import SectionTitle from '../../../components/content/SectionTitle';
import AlertModal from '../../../components/content/AlertModal';
import SpreedlyService from '../../../services/SpreedlyService';
import {
  SpreedlyPostOrderResponse,
  SpreedlyThreeDSStatusEvent,
} from '../../../types/Spreedly';
import ApiValidationError from '../../../models/classes/ApiValidationError';
import ServiceError from '../../../models/classes/ServiceError';
import {
  getCardBrandFromPaymentMethod,
  getCardExpDateFromPaymentMethod,
  getCardLast4FromPaymentMethod,
  getIntentFromResponse,
  getPaymentIntentIdFromResponse,
  getPaymentMethodIdFromIntent,
  getPaymentMethodObject,
  getSetupIntentIdFromResponse,
} from '../../../utils/stripe/intentParsing';

export const EditPaymentViewTestId = 'EditPaymentView-views-pages';

export type EditPaymentFormData = {
  paymentMethodId: string;
  creditCard: {
    cardNumber: string;
    cardHolderName: string;
    expMonth: string;
    expYear: string;
    cvv: string;
  };
  purchaseOrder?: {
    poNumber: string;
  };
  stripe?: boolean;
};

export type EditPaymentViewProps = {
  onEditComplete: () => void;
  onScrollTopOfPage?: () => void;
};

const currentMonth = new Date().getMonth() + 1;

const EditPaymentView: React.FC<EditPaymentViewProps> = observer(
  ({ onEditComplete, onScrollTopOfPage }: EditPaymentViewProps) => {
    const selectedPaymentMethodIdRef = useRef<string | null>(null);
    const rootStore = useMst();
    const { configuration, customer, cart } = rootStore;
    const [formErrors, setFormErrors] = useState<string[]>([]);
    const [showGiftCardModal, setShowGiftCardModal] = useState<boolean>(false);
    const [clearGiftCard, setClearGiftCard] = useState<boolean>(false);
    const applyButtonEnabledRef = useRef<boolean>(false);

    const handleOnProfileSelected = (
      method: ConfigEnabledPaymentMethodContract,
      paymentConfig: PaymentProfileUpdateConfig,
      selectedProfileId: number
    ): void => {
      cart.setNeedsNewPciToken(true);
      customer.setSelectedPaymentProfileId(selectedProfileId);
      cart.updatePaymentProfile(method, paymentConfig);
      onEditComplete();
    };

    const handlePayPalOnClick = async (
      data: PayPalOnClickData
    ): Promise<void> => {
      cart.paypalInfo.setIsVenmoPending(
        data.fundingSource?.toLowerCase() === 'venmo'
      );
    };

    const handlePayPalOnApprove = async (
      data: PayPalOnApproveData
    ): Promise<void> => {
      cart.setNeedsNewPciToken(false);
      cart.paypalInfo.setIsVenmo(cart.paypalInfo.isVenmoPending);
      rootStore.send({ type: 'PAYPAL_ON_APPROVED', paypalParams: data });
    };

    const handleAmazonPayOnLoaded = (): void => {
      rootStore.setAmazonLoaded(true);
    };

    const handleAmazonPayOnAuthorize = async (
      accessToken: string
    ): Promise<void> => {
      cart.setNeedsNewPciToken(false);
      rootStore.send({
        type: 'AMAZONPAY_ON_AUTHORIZE',
        amazonPayToken: accessToken,
      });
    };

    const paymentSchema = getPaymentSchema(
      currentMonth,
      Boolean(configuration.paypalExpress.acpClientToken),
      cart.totals.grandTotal > 0,
      configuration.features.enableSpreedly
    );

    const getDefaultPaymentMethodId = (): string => {
      const { enabledPaymentMethodsWithFormContext } = configuration;
      const { paymentProfile } = cart.cartData;

      if (!paymentProfile.id) {
        return configuration.defaultPaymentMethodId;
      }

      const payPalMethod = enabledPaymentMethodsWithFormContext.find(
        method => method.methodType.toLowerCase() === 'paypal express upgrade'
      );

      if (
        paymentProfile.id.toString() === payPalMethod?.id &&
        paymentProfile.card?.last4
      ) {
        return '-1';
      }

      return paymentProfile.id.toString();
    };

    const editPaymentViewForm = useForm<EditPaymentFormData>({
      resolver: yupResolver(paymentSchema),
      shouldUnregister: true,
      defaultValues: {
        paymentMethodId: getDefaultPaymentMethodId(),
        creditCard: {
          cardNumber: '',
        },
        purchaseOrder: {
          poNumber: cart.cartData.paymentProfile.poNumber ?? '',
        },
      },
    });

    const handleOnTokenRequest = async (
      creditCardData: EditPaymentFormData,
      cardTypeName: string
    ): Promise<string | null> => {
      const { cardNumber, cardHolderName, expMonth, expYear, cvv } =
        creditCardData.creditCard;
      cart.setIsTokenizingCard(true);
      const token = await cart.tokenizeCard({
        cardNumber: cardNumber.replace(/-|\s/g, ''),
        cardHolderName,
        expireMonth: expMonth,
        expireYear: expYear,
        cvv: cvv as string,
        cardTypeName,
      });
      cart.setIsTokenizingCard(false);
      return token;
    };

    const validateCC = async (
      creditCardData: EditPaymentFormData
    ): Promise<void> => {
      const cardTypeName = UtilityService.getCreditCardType(
        creditCardData.creditCard.cardNumber
      );
      if (!cardTypeName) {
        editPaymentViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.CARD_NUMBER_INVALID_MESSAGE,
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.CARD_NUMBER_INVALID_MESSAGE);
      }
      const matchingMethod = configuration.cardMethods.find(
        method => method.method.toLowerCase() === cardTypeName.toLowerCase()
      );
      if (!matchingMethod) {
        editPaymentViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.CARD_NOT_ACCEPTED_MESSAGE.replace(
              '{0}',
              cardTypeName
            ),
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.CARD_NOT_ACCEPTED_MESSAGE);
      }

      const token = await handleOnTokenRequest(creditCardData, cardTypeName);

      if (!token) {
        editPaymentViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.TOKEN_FAILURE_MESSAGE,
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.TOKEN_FAILURE_MESSAGE);
      }

      const { cardNumber, cardHolderName, expMonth, expYear, cvv } =
        creditCardData.creditCard;
      const last4 = cardNumber.slice(cardNumber.length - 4);
      const cardConfig: CartPaymentProfileCardContract = {
        pCIaaSId: token,
        cardBrand: null,
        cardNumber: `************${last4}`,
        last4,
        cardType: matchingMethod.id,
        cardTypeName,
        cvv: cvv ?? null,
        holdersName: cardHolderName,
        expMonth: `0${expMonth}`.slice(-2),
        expYear: `20${expYear}`.slice(-4),
        id: null,
        issueMonth: null,
        issueYear: null,
        issueNumber: null,
      };
      cart.updatePaymentProfile(matchingMethod, { cardConfig });
    };

    const handleUpdatingStripeIntent = async (currentPaymentMethod: string) => {
      if (
        !configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) ||
        !configuration.hasStripePaymentMethodEnabled ||
        currentPaymentMethod !== APIValues.STRIPE_METHOD
      ) {
        return undefined;
      }
      const stripeResponse = await rootStore.updateStripeIntentFlow();

      if (stripeResponse?.error) {
        return stripeResponse;
      }

      const intent = getIntentFromResponse(stripeResponse);
      if (!intent) {
        return stripeResponse;
      }
      const paymentMethod = getPaymentMethodObject(intent);
      cart.updateStripeInfo({
        paymentIntentId: getPaymentIntentIdFromResponse(stripeResponse),
        paymentMethodId: getPaymentMethodIdFromIntent(intent),
        cardBrand: getCardBrandFromPaymentMethod(paymentMethod),
        last4: getCardLast4FromPaymentMethod(paymentMethod),
        setupIntentId: getSetupIntentIdFromResponse(stripeResponse),
        isSavedCard: false,
        isWallet: false,
        ...getCardExpDateFromPaymentMethod(paymentMethod),
      });

      return stripeResponse;
    };

    const submitNewPaymentMethod = async (
      editPaymentFormData: EditPaymentFormData
    ): Promise<void> => {
      setShowGiftCardModal(false);
      setClearGiftCard(false);
      setFormErrors([]);
      cart.setUpdatingCart(true);

      if (cart.totals.grandTotal === 0) {
        cart.setUpdatingCart(false);
        onEditComplete();
        return;
      }

      if (applyButtonEnabledRef.current) {
        setShowGiftCardModal(true);
        cart.setUpdatingCart(false);
        return;
      }

      if (editPaymentFormData.paymentMethodId === '-1') {
        if (rootStore.payPalAcpOnSubmit) {
          await rootStore.payPalAcpOnSubmit();
          cart.setUpdatingCart(false);
          return;
        }
        if (configuration.features.enableSpreedly) {
          rootStore.validateSpreedlyInput();
          return;
        }
        try {
          await validateCC(editPaymentFormData);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
          cart.setUpdatingCart(false);
          return;
        }
      } else {
        const requiredAction = configuration.payments.getRequired3rdPartyAction(
          editPaymentFormData.paymentMethodId
        );
        if (requiredAction && !cart.hasRecurringBillingItem) {
          setFormErrors([requiredAction]);
          cart.setUpdatingCart(false);
          return;
        }

        const matchingMethod = configuration.getMatchingPaymentMethod(
          editPaymentFormData.paymentMethodId
        );
        if (
          selectedPaymentMethodIdRef.current === null &&
          matchingMethod.methodType === 'Credit Card'
        ) {
          setFormErrors([UIConstants.CHOOSE_PAYMENT_METHOD]);
          cart.setUpdatingCart(false);
          return;
        }

        const stripeResponse = await handleUpdatingStripeIntent(
          matchingMethod.methodType
        );

        if (stripeResponse?.error) {
          cart.setUpdatingCart(false);
          return;
        }

        const configs: PaymentProfileUpdateConfig = {
          poNumber: editPaymentFormData?.purchaseOrder?.poNumber,
        };

        if (stripeResponse) {
          configs.stripeConfig = cart.cartData.stripeInfo;
        }
        cart.setPaymentMethodType(matchingMethod.methodType);
        cart.updatePaymentProfile(matchingMethod, configs);
      }
      cart.setUpdatingCart(false);
      customer.setSelectedPaymentProfileId();
      onEditComplete();
    };

    const handleApiValidationError = (error: ApiValidationError): void => {
      setFormErrors(error.extractErrorMessages());
      onScrollTopOfPage?.();
    };

    const handleGenericError = (error: ServiceError | Error): void => {
      setFormErrors([error.message]);
      onScrollTopOfPage?.();
    };

    const handleSpreedlyTransaction = (
      spreedlyTransaction: SpreedlyPostOrderResponse,
      orderId: string
    ) => {
      // Possible status values: 'succeeded', 'failed', 'pending', 'error'
      switch (spreedlyTransaction.status) {
        case null:
          // a null value for status is a success when not cc (we have an orderid at this point)
          rootStore.send({ type: 'ORDER_PLACED', orderId });
          break;
        case SpreedlyValues.SPREEDLY_3DS_SUCCEEDED:
          // Spreedly transaction succeed without needing 3DS authentication!
          rootStore.send({ type: 'ORDER_PLACED', orderId });
          break;
        case SpreedlyValues.SPREEDLY_3DS_PENDING:
          // user will have to authenticate
          SpreedlyService.start3DSLifecycle(
            spreedlyTransaction.transactionToken,
            configuration.payments.spreedlyEnvironmentKey
          );

          rootStore.send({ type: 'SPREEDLY_3DS_LIFECYCLE_STARTED' });
          break;
        default:
          // eslint-disable-next-line no-console
          console.log(`Event ${spreedlyTransaction.status} not handled`);
      }
    };

    const tryPlaceOrder = async (): Promise<void> => {
      setFormErrors([]);
      let orderId: string | undefined;
      let spreedlyTransaction;
      let requiresAction: boolean | undefined;

      cart.setUpdatingCart(true);

      try {
        if (configuration.features.enableSpreedly) {
          spreedlyTransaction = await cart.spreedlyPlaceOrder();
          orderId = spreedlyTransaction.orderId;
        } else {
          const orderResponse = await cart.placeOrder();
          orderId = orderResponse.orderId;
          requiresAction = orderResponse.requiresAction;
          if (requiresAction && orderResponse?.transactionToken) {
            cart.updateStripeInfo({
              clientSecretKey: orderResponse.transactionToken,
            });
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError) {
          handleApiValidationError(error);
        } else {
          handleGenericError(error);
        }
      }

      if (orderId) {
        try {
          if (requiresAction === true) {
            const { error } = await rootStore.handleStripeNextAction(
              cart.stripeInfo.clientSecretKey
            );

            if (error) {
              handleGenericError(error);
              cart.setUpdatingCart(false);
              return;
            }
          }

          const paymentProfileToSave = {
            ...cart.cartData.paymentProfile,
            id: customer.selectedPaymentProfileId ?? -1,
            preferred: true,
            card: {
              ...(cart.cartData.paymentProfile.card || {}),
            },
          } as CartPaymentProfileContract;

          await customer.saveNewPaymentProfile(paymentProfileToSave);

          if (spreedlyTransaction) {
            handleSpreedlyTransaction(spreedlyTransaction, orderId);
          } else {
            rootStore.send({ type: 'ORDER_PLACED', orderId });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }
      cart.setUpdatingCart(false);
    };

    const handleSpreedlyOnThreeDSStatus = async (
      event: SpreedlyThreeDSStatusEvent
    ) => {
      // Possible event action values: 'succeeded', 'challenge', 'finalization-timeout', 'error'
      switch (event.action) {
        case SpreedlyValues.SPREEDLY_3DS_CHALLENGE:
          // show the challenge-modal
          cart.setShowSpreedlyChallengeModal(true);
          break;
        case SpreedlyValues.SPREEDLY_3DS_SUCCEEDED:
          // finish checkout and redirect to success page
          cart.setShowSpreedlyChallengeModal(false);

          cart.updateSpreedlyTransactionDetails({
            transactionToken: event.token,
            challengeResponse: event.action,
          });

          await tryPlaceOrder();
          break;
        default:
          rootStore.send({ type: 'ORDER_ERROR' });
      }
    };

    const handleSpreedlyOnValidation = (
      inputProperties: spreedly.SpreedlyFieldEventInputProperties
    ) => {
      if (inputProperties.validCvv && inputProperties.validNumber) {
        const formValues = editPaymentViewForm.getValues();
        rootStore.tokenizeSpreedlyCard({
          cardHolderName: formValues.creditCard.cardHolderName,
          expMonth: formValues.creditCard.expMonth,
          expYear: formValues.creditCard.expYear,
        });
        return;
      }
      cart.setUpdatingCart(false);
    };

    const handleSpreedlyOnPaymentMethod = (
      token: string,
      paymentMethod: spreedly.SpreedlyPaymentMethod
    ): void => {
      const convertedCardType = SpreedlyService.convertSpreedlyToV1CardType(
        paymentMethod.card_type
      );
      const matchingMethod = configuration.cardMethods.find(
        method =>
          method.method.toLowerCase() === convertedCardType.toLowerCase()
      );
      if (!matchingMethod) {
        setFormErrors([
          UIConstants.CARD_NOT_ACCEPTED_MESSAGE.replace(
            '{0}',
            convertedCardType
          ),
        ]);
        cart.setUpdatingCart(false);
        return;
      }
      const formValues = editPaymentViewForm.getValues();
      const last4 = paymentMethod.last_four_digits;
      const cardConfig: CartPaymentProfileCardContract = {
        pCIaaSId: null,
        cardBrand: null,
        spreedlyPaymentToken: token,
        paymentMethodFingerprint: paymentMethod.fingerprint,
        cardNumber: `************${last4}`,
        last4,
        cardType: matchingMethod.id,
        cardTypeName: matchingMethod.method,
        cvv: null,
        holdersName: paymentMethod.full_name,
        expMonth:
          paymentMethod.month?.toString()?.padStart(2, '0') ??
          formValues.creditCard.expMonth,
        expYear:
          paymentMethod.year?.toString() ?? formValues.creditCard.expYear,
        id: null,
        issueMonth: null,
        issueYear: null,
        issueNumber: null,
        browserInfo:
          configuration.payments.isSpreedly3DSEnabled &&
          configuration.features.enableSpreedly
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              Spreedly.ThreeDS.serialize('03', configuration.headerAccept)
            : null,
        transactionToken: null,
        challengeResponse: null,
      };
      cart.setNeedsNewPciToken(false);
      cart.updatePaymentProfile(matchingMethod, { cardConfig });
      customer.setSelectedPaymentProfileId();
      cart.setUpdatingCart(false);
      onEditComplete();
    };

    const handleSpreedlyOnErrors = (errors: spreedly.SpreedlyError[]): void => {
      if (errors.length) {
        const errorList = errors.map(error => error.message);
        setFormErrors([UIConstants.TOKEN_FAILURE_MESSAGE, ...errorList]);
      }
      cart.setUpdatingCart(false);
    };

    const spreedlyEventHandlers = configuration.features.enableSpreedly
      ? {
          onValidation: handleSpreedlyOnValidation,
          onPaymentMethod: handleSpreedlyOnPaymentMethod,
          onThreeDSStatus: handleSpreedlyOnThreeDSStatus,
          onErrors: handleSpreedlyOnErrors,
        }
      : undefined;

    const handlePayPalAcpOnCardValidated = (
      data: PayPalAcpHostedFieldsResponseData
    ): void => {
      rootStore.setPayPalAcpDetails(data);
      onEditComplete();
    };

    const handlePaymentMethodChanged = (
      selectedPaymentMethodId: string | null
    ) => {
      selectedPaymentMethodIdRef.current = selectedPaymentMethodId;
      setFormErrors([]);
    };

    const setApplyButtonEnabled = (enabled: boolean) => {
      applyButtonEnabledRef.current = enabled;
    };

    const handleGiftCardModalContinueButton = () => {
      setClearGiftCard(true);
      editPaymentViewForm.handleSubmit(submitNewPaymentMethod)();
    };

    const acpSetupMethods: PayPalAcpMethods | undefined =
      cart.isUsingPayPalAcp &&
      configuration.paypalExpress.showCreditDebitCardLogos
        ? {
            onCardValidated: handlePayPalAcpOnCardValidated,
          }
        : undefined;

    return (
      <div className="flex flex-col" data-testid={EditPaymentViewTestId}>
        <div className="text-xs">
          {cart.cartData.paymentProfile.id && (
            <InternalLink onClick={onEditComplete}>Go back</InternalLink>
          )}
          {!cart.cartData.paymentProfile.id &&
            !configuration.paypalExpress.isDefaultPayment && (
              <FormErrorLabel
                errors={[
                  'Your account does not have a credit card saved.',
                  'Please choose a payment method to continue.',
                ]}
              />
            )}
          <SectionTitle>Payment</SectionTitle>
        </div>
        <FormProvider {...editPaymentViewForm}>
          {process.env.NODE_ENV === 'development' && (
            <DevTool
              control={editPaymentViewForm.control}
              placement="top-right"
            />
          )}
          <form
            onSubmit={editPaymentViewForm.handleSubmit(submitNewPaymentMethod)}
          >
            {showGiftCardModal && (
              <AlertModal
                handleContinueButton={handleGiftCardModalContinueButton}
                headerText="Gift Card Not Applied"
                bodyText="You’ve entered a gift card without applying it. Are you
                  sure you want to continue?"
              />
            )}

            <FormErrorLabel errors={formErrors} />
            {customer.isLoggedIn ? (
              <AuthenticatedPaymentMethodForm
                cartTotal={cart.totals.grandTotal}
                onPaymentMethodChanged={handlePaymentMethodChanged}
                hasRecurringBillingItem={cart.hasRecurringBillingItem}
                paymentProfiles={customer.filteredPaymentProfiles}
                paymentMethods={
                  configuration.enabledPaymentMethodsWithFormContext
                }
                payPalButtonMethods={{
                  createOrder: rootStore.createPayPalOrder,
                  onApprove: handlePayPalOnApprove,
                  onClick: handlePayPalOnClick,
                }}
                payPalAcpMethods={acpSetupMethods}
                amazonPayButtonProps={{
                  clientId: configuration.amazonPay.clientId,
                  sellerId: configuration.amazonPay.sellerId,
                  isSandbox: configuration.amazonPay.isSandbox,
                  isLoaded: rootStore.isAmazonPayLoaded,
                  onAuthorize: handleAmazonPayOnAuthorize,
                  onLoaded: handleAmazonPayOnLoaded,
                }}
                spreedlyEventHandlers={spreedlyEventHandlers}
                onProfileSelected={handleOnProfileSelected}
                hasGiftCards={cart.giftCards.length > 0}
              />
            ) : (
              <PaymentMethodForm
                cartTotal={cart.totals.grandTotal}
                hasGiftCards={cart.giftCards.length > 0}
                onPaymentMethodChanged={handlePaymentMethodChanged}
                hasRecurringBillingItem={cart.hasRecurringBillingItem}
                paymentMethods={
                  configuration.enabledPaymentMethodsWithFormContext
                }
                payPalButtonMethods={{
                  createOrder: rootStore.createPayPalOrder,
                  onApprove: handlePayPalOnApprove,
                }}
                payPalAcpMethods={acpSetupMethods}
                spreedlyEventHandlers={spreedlyEventHandlers}
                amazonPayButtonProps={{
                  clientId: configuration.amazonPay.clientId,
                  sellerId: configuration.amazonPay.sellerId,
                  isSandbox: configuration.amazonPay.isSandbox,
                  isLoaded: rootStore.isAmazonPayLoaded,
                  onAuthorize: handleAmazonPayOnAuthorize,
                  onLoaded: handleAmazonPayOnLoaded,
                }}
              />
            )}
            {configuration.features.enableGiftCards && (
              <>
                <Spacer />
                <GiftCardSection
                  onGiftCardsUpdating={() => setFormErrors([])}
                  setApplyButtonEnabled={setApplyButtonEnabled}
                  clearGiftCard={clearGiftCard}
                />
              </>
            )}
            <Button
              center
              className="max-w-md m-auto"
              isOtherActionPending={cart.isTokenizingCard || cart.updatingCart}
            >
              Continue
            </Button>
          </form>
        </FormProvider>
      </div>
    );
  }
);

export default EditPaymentView;
